import chatMessageTextStyles from 'css/src/components/agent/ChatMessageText.module.scss';

import React from 'react';
import { VideoData } from '@lumen5/luminary';

import MarkdownWithDiff from '../components/MarkdownWithDiff';
import type { CustomRendererComponentProps, ToolDefinition, ToolExecutionContext } from '../types';

import { AddChartOverlayTool } from './AddChartOverlayTool';
import { AddChartSceneTool } from './AddChartSceneTool';
import { EditChartTool } from './EditChartTool';
import { resolveSceneHandle, sceneDisplayId } from './scene-addressing';
import { VideoStateTool } from './VideoStateTool';

import { checkpointVideo } from 'js/src/data/video-data';
import {
    addChartAssetToSceneOperation,
    applyGeneratedChartConfigOperation,
    getDefaultChartConfig,
} from 'js/src/libs/chart-asset-add';
import { SUPPORTED_CHART_TYPES, type SupportedChartType } from 'js/src/libs/default-chart-configs';
import { saveRawLuminary } from 'js/src/luminary';
import { makeOperationsCallable } from 'js/src/luminary/luminary-hooks';

interface AddChartArgs {
    scene_id: string;
    chart_type: SupportedChartType;
    data: string;
    style_index?: number;
    position?: { x: number; y: number };
}

export class AddChartTool extends VideoStateTool<AddChartArgs> {
    public static readonly toolName = 'add_chart';

    public override customRenderer = AddChartResultRenderer;

    public override getToolDefinition(): ToolDefinition {
        return {
            type: 'function',
            function: {
                name: AddChartTool.toolName,
                description: `Overlay a data chart (bar, line, or pie) ON TOP OF an existing scene's content, without clearing it.

**PREFER \`${AddChartSceneTool.toolName}\` FOR MOST CHART REQUESTS.** A chart is added at near-full-scene size and renders on top, so on a normal content scene it will cover the scene's existing text and media. When the user just wants "a chart" / "a chart of X", use \`${AddChartSceneTool.toolName}\` — it makes a clean, dedicated chart scene. Only use \`${AddChartTool.toolName}\` when the user explicitly wants the chart layered over a specific scene's existing media/visual (e.g. "put a small chart over this background"), and be ready to resize/reposition it so it doesn't bury the scene's text.

For voiceover/talking-head (voicedriven) scene groups, do NOT use this — use \`${AddChartOverlayTool.toolName}\`, which places a chart cutaway over a chosen span of transcript words. (If used here anyway, the underlying op auto-creates a heuristically-placed cutaway, which you usually don't want.)

**DATA — describe what the chart should show, and include the actual numbers/labels when you have them** (e.g. "coin-flip outcomes — Heads 6, Tails 4", or "quarterly revenue: Q1 1.2M, Q2 1.8M, Q3 1.5M, Q4 2.1M"). If you don't have figures, describe the data and the generator supplies it (it may estimate). Keep values in the form you want shown — percentages stay percentages, counts stay counts. \`style_index\` picks the visual design (use 0 unless asked); the chart fills that design with your data and takes on as many bars/lines/slices as the data needs, regardless of the design's placeholder shape. After adding, check a screenshot and fix labels/contrast/type with \`${EditChartTool.toolName}\` if needed.

<example>
User: "add a pie chart of global market share between the top phone brands" (general request) → use ${AddChartSceneTool.toolName}, NOT ${AddChartTool.toolName}.
</example>

<example>
User: "overlay a small bar chart on top of this product photo scene"
${AddChartTool.toolName} with scene_id, chart_type: "bar", data: "...", and a position/smaller placement so it doesn't cover the text.
</example>`,
                parameters: {
                    type: 'object',
                    properties: {
                        scene_id: {
                            type: 'string',
                            description: 'The scene_id from the video state.',
                        },
                        chart_type: {
                            type: 'string',
                            description: 'The kind of chart to create.',
                            enum: [...SUPPORTED_CHART_TYPES],
                        },
                        data: {
                            type: 'string',
                            description:
                                'A description of what the chart should show, with any actual numbers/labels included inline (e.g. "coin-flip outcomes — Heads 6, Tails 4"). Include figures when you have them; otherwise describe the data and the generator supplies it.',
                        },
                        style_index: {
                            type: 'number',
                            description:
                                'Optional preset style variant for the chart type (0-based). Defaults to 0.',
                            default: 0,
                        },
                        position: {
                            type: 'object',
                            description:
                                'Optional initial position (scene coordinates) for the chart center. Defaults to centered, near full-scene size.',
                            properties: {
                                x: { type: 'number' },
                                y: { type: 'number' },
                            },
                            required: ['x', 'y'],
                        },
                    },
                    required: ['scene_id', 'chart_type', 'data'],
                },
            },
        };
    }

    public override getToolDisplayName(): string {
        return 'Add Chart';
    }

    public get statusIcon(): string {
        return 'bar_chart';
    }

    public async execute(args: AddChartArgs, context: ToolExecutionContext) {
        const { chart_type, data, style_index = 0, position } = args;
        const { parentActionGroup } = context;

        try {
            const { sceneIndex: scene_index, sceneShortId } = resolveSceneHandle(
                this.luminary,
                this.getState(),
                args,
            );

            const config = getDefaultChartConfig(chart_type, style_index);
            if (!config) {
                return `Error: No ${chart_type} chart style at index ${style_index}. Use style_index 0 if unsure.`;
            }

            const scene = this.luminary.scenes[scene_index];

            // Capture the target scene's visible editable text before adding the chart, so we can
            // warn if the full-size chart ends up covering it (a common way charts bury design text).
            const coveredTextNames = scene
                .getAssets()
                .filter(
                    (asset) =>
                        VideoData.isTextAsset(asset) &&
                        asset.isEditable &&
                        asset.text.trim().length > 0,
                )
                .map((asset) => asset.name);

            const { addChartAssetToScene, applyGeneratedChartConfig } = makeOperationsCallable(
                {
                    addChartAssetToSceneOperation,
                    applyGeneratedChartConfigOperation,
                },
                this.luminary,
                this.dispatch,
                this._getState,
            );

            const added = addChartAssetToScene(scene.id, chart_type, config, position);
            if (!added) {
                return `Error: Could not add a chart to scene ${sceneShortId}`;
            }

            const { sceneId, assetId } = added;

            const status = await applyGeneratedChartConfig({
                sceneId,
                assetId,
                chartType: chart_type,
                data,
                extractChartDataFromResult: true,
                parentActionGroup,
            });

            const landingIndex = this.luminary.scenes.findIndex((s) => s.id === sceneId);
            const landingSceneId = sceneDisplayId(this.luminary, sceneId);
            const landingScene = this.luminary.getSceneById(sceneId);
            const assetName =
                landingScene?.getAsset(assetId, VideoData.CHART_ASSET_TYPE)?.name ?? 'chart';

            // applyGeneratedChartConfig only checkpoints on success. When data
            // generation fails the chart is still on the scene, so checkpoint it
            // here and report partial success rather than losing the addition.
            if (status !== 'success') {
                this.dispatch(
                    checkpointVideo({
                        video_data: saveRawLuminary(this.luminary),
                        systemTriggered: false,
                        parentActionGroup,
                    }),
                );
            }

            const placement =
                landingIndex !== scene_index
                    ? ` (placed on a new overlay scene ${landingSceneId} so it doesn't cover the speaker)`
                    : '';

            // Only warn about covered text when the chart landed on the original scene (not a
            // fresh voicedriven overlay, which is created empty).
            const coverageWarning =
                landingIndex === scene_index && coveredTextNames.length > 0
                    ? ` Warning: this scene has text (${coveredTextNames.join(', ')}) that the full-size chart now covers. If you wanted a standalone chart, delete this and use ${AddChartSceneTool.toolName}; otherwise resize/reposition the chart with ${EditChartTool.toolName} so the text stays visible.`
                    : '';

            if (status === 'success') {
                return `Success: Added ${chart_type} chart "${assetName}" to scene ${landingSceneId}${placement} and populated it with data.${coverageWarning}`;
            }
            return `Success: Added ${chart_type} chart "${assetName}" to scene ${landingSceneId}${placement}, but data generation failed so the chart shows placeholder data. Use ${EditChartTool.toolName} to set the data.${coverageWarning}`;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
            return `Error adding chart: ${errorMessage}`;
        }
    }
}

const AddChartResultRenderer: React.FC<CustomRendererComponentProps> = ({ isLoading, result }) => {
    if (isLoading) {
        return null;
    }
    return (
        <div className={chatMessageTextStyles['chat-message-text']}>
            <MarkdownWithDiff>{result}</MarkdownWithDiff>
        </div>
    );
};
